/**
 * Fleet portal access constants shared by the console (invite drawer, client
 * record) and the server (invite route, portal access service) — CUR-1049.
 */

import type { ProductTypeCategory } from '../../prisma/enums';

/**
 * Fleet capability — the portal, escalations and delegated-authority
 * decisions — belongs to the FLEET product category only. MOTOR keeps the
 * vehicle features (assessment, inspection) but a motor claim for an
 * individual has no fleet client behind it (decision, 4 Sep 2026).
 */
export const FLEET_PRODUCT_CATEGORY = 'FLEET' satisfies ProductTypeCategory;

export function isFleetProductCategory(category: ProductTypeCategory | null | undefined): boolean {
    return category === FLEET_PRODUCT_CATEGORY;
}

/** Lifecycle of a portal user as shown on the client record's Portal access card. */
export const PORTAL_USER_STATUSES = ['INVITED', 'ACTIVE', 'REVOKED'] as const;
export type PortalUserStatus = (typeof PORTAL_USER_STATUSES)[number];

/** Largest per-user or per-client authority the UI accepts (ex GST, matches Decimal(12,2)). */
export const MAX_FLEET_AUTHORITY_LIMIT = 9_999_999_999.99;

/**
 * Reserved for a future revision-safe Clear21 approval relay (CUR-1052).
 * No runtime code reads this flag today: the relay is unconditionally disabled.
 * Do not enable dispatch until Clear21 atomically enforces the approved quote
 * fingerprint and amount; API permissions and a flag alone are insufficient.
 */
export const ENABLE_CLEAR21_APPROVAL_WRITEBACK_FLAG = 'enable-clear21-approval-writeback';

/**
 * The platform default for an organisation's delegated authority (ex GST),
 * seeded on the `fleet_default_delegated_authority` setting. A client without
 * its own authority limit is measured against the organisation's value, which
 * starts here (decision, JLo, 7 Sep 2026: seed at $5,000).
 */
export const DEFAULT_FLEET_DELEGATED_AUTHORITY = 5000;

/** Longest decline reason or query the portal accepts. */
export const AUTHORISATION_NOTE_MAX_LENGTH = 2000;

/**
 * Sections of the portal claim page a tenant can switch off without a deploy
 * (CUR-1069: visibility rules are configuration). The header — claim
 * reference, status, vehicle — is never optional.
 */
export const PORTAL_CLAIM_VIEW_SECTIONS = [
    'claimStates',
    'claimDetails',
    'repairProgression',
    'repairAssessment',
    'damageScan',
] as const;
export type PortalClaimViewSection = (typeof PORTAL_CLAIM_VIEW_SECTIONS)[number];

export type FleetPortalClaimViewConfiguration = {
    sections: Record<PortalClaimViewSection, boolean>;
};

/** Shipped default: the whole strip list. */
export const DEFAULT_FLEET_PORTAL_CLAIM_VIEW: FleetPortalClaimViewConfiguration = {
    sections: {
        claimStates: true,
        claimDetails: true,
        repairProgression: true,
        repairAssessment: true,
        damageScan: true,
    },
};
